"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { FormField } from "@/components/molecules/FormField";
import { Button } from "@/components/atoms/button";
import { Input } from "@/components/atoms/input";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { validateEmail, isValidHttpUrl } from "@/lib/forms/validation";
import { deriveUrlFromEmail } from "@/lib/forms/deriveUrlFromEmail";
import type { LeadCaptureFormProps, LeadCaptureSubmission } from "./LeadCaptureForm.props";
type FormStatus = "idle" | "submitting" | "success" | "error";
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  website: string;
}
const INITIAL_VALUES: FormValues = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  website: ""
};

/**
 * Invariant: `prefillUrl` is always `deriveUrlFromEmail(prefillEmail)`. Do not
 * pass an independently-sourced URL — the prefill-mode visibility and required-
 * ness logic keys off `prefillEmail`, so a divergent `prefillUrl` would not
 * trigger the prefill UX even when present.
 */
function LeadCaptureFormInner({
  prefillEmail,
  prefillUrl,
  heading,
  description,
  submitLabel = "Get Your Free Audit",
  formPurpose = "Lead Capture Form Submission",
  successHeading = "Thank You!",
  successMessage = "We'll be in touch shortly.",
  errorMessage = "Something went wrong. Please try again.",
  onSubmit,
  className
}: LeadCaptureFormProps & {
  prefillEmail: string | null;
  prefillUrl: string | null;
}) {
  const [values, setValues] = useState<FormValues>(() => ({
    ...INITIAL_VALUES,
    email: prefillEmail ?? "",
    website: prefillUrl ?? ""
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<FormStatus>("idle");
  const [honeypot, setHoneypot] = useState("");

  // True when a valid email was prefilled AND the user hasn't changed it away
  const isEmailPrefillMode = prefillEmail !== null && values.email === prefillEmail;
  const handleChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValues = {
      ...values,
      [field]: e.target.value
    };
    setValues(nextValues);
    if (errors[field]) {
      const {
        [field]: _,
        ...nextErrors
      } = errors;
      setErrors(nextErrors);
    }
  };
  const validate = (): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};
    if (!isEmailPrefillMode) {
      if (!values.firstName.trim()) fieldErrors.firstName = "First name is required";
      if (!values.lastName.trim()) fieldErrors.lastName = "Last name is required";
      if (!values.company.trim()) fieldErrors.company = "Company name is required";
    }
    if (!values.email.trim()) {
      fieldErrors.email = "Email is required";
    } else if (!validateEmail(values.email)) {
      fieldErrors.email = "Please enter a valid email address";
    }
    if (isEmailPrefillMode) {
      if (!values.website.trim()) {
        fieldErrors.website = "Website URL is required";
      } else if (!isValidHttpUrl(values.website)) {
        fieldErrors.website = "Please enter a valid URL";
      }
    }
    return fieldErrors;
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();

    // Honeypot check — silently "succeed" if filled
    if (honeypot) {
      setStatus("success");
      return;
    }
    const fieldErrors_0 = validate();
    if (Object.keys(fieldErrors_0).length > 0) {
      setErrors(fieldErrors_0);
      setStatus("idle");
      return;
    }
    if (!onSubmit) return;
    setStatus("submitting");
    setErrors({});
    const submission: LeadCaptureSubmission = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      company: values.company.trim(),
      websiteUrl: values.website.trim(),
      formPurpose
    };
    try {
      const result = await onSubmit(submission);
      setStatus(result.success ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  // Success state — replace form with confirmation
  if (status === "success") {
    return <Box className={cn("flex flex-col gap-4", className)}>
        <Text as="p" className="text-xl font-bold lg:text-2xl">
          {successHeading}
        </Text>
        <Text as="p" className="text-sm leading-relaxed opacity-80">
          {successMessage}
        </Text>
      </Box>;
  }
  return <Box className={cn("flex flex-col gap-6", className)}>
      {heading && <Text as="p" className="text-xl font-bold lg:text-2xl">
          {heading}
        </Text>}

      {description && <Text as="p" className="text-sm leading-relaxed opacity-80">
          {description}
        </Text>}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit} aria-label={heading || formPurpose || "Lead capture form"} role="form">
        {/* Honeypot — visually hidden, catches bots. Uses Input atom (not raw <input>) */}
        <Box className="absolute h-0 w-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="field-_hp">Do not fill this field</label>
          <Input id="field-_hp" name="_hp" type="text" tabIndex={-1} autoComplete="off" aria-label="Do not fill this field" value={honeypot} onChange={e_1 => setHoneypot(e_1.target.value)} />
        </Box>

        {/* Row 1: First Name + Last Name — hidden in prefill mode */}
        {!isEmailPrefillMode && <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField name="firstName" label="First Name" placeholder="Jane" required autoComplete="given-name" value={values.firstName} onChange={handleChange("firstName")} error={errors.firstName} />
            <FormField name="lastName" label="Last Name" placeholder="Doe" required autoComplete="family-name" value={values.lastName} onChange={handleChange("lastName")} error={errors.lastName} />
          </Box>}

        {/* Row 2: Email — always visible */}
        <FormField name="email" label="Work Email" type="email" placeholder="jane@company.com" required autoComplete="email" value={values.email} onChange={handleChange("email")} error={errors.email} />

        {/* Row 3: Company — hidden in prefill mode */}
        {!isEmailPrefillMode && <FormField name="company" label="Company Name" placeholder="Acme Corp" required autoComplete="organization" value={values.company} onChange={handleChange("company")} error={errors.company} />}

        {/* Row 4: Website URL — always visible. Required in prefill mode. */}
        <FormField name="website" label="Website URL" type="url" placeholder="https://yourwebsite.com" required={isEmailPrefillMode} autoComplete="url" value={values.website} onChange={handleChange("website")} error={errors.website} />

        {/* Error message */}
        {status === "error" && <Text as="p" className="text-destructive text-sm" role="alert">
            {errorMessage}
          </Text>}

        {/* Submit */}
        <Button type="submit" size="lg" className="mt-2 w-full" disabled={status === "submitting"} aria-label={submitLabel || "Submit"}>
          {status === "submitting" ? <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <Text className="sr-only">Submitting…</Text>
            </> : submitLabel}
        </Button>
      </form>
    </Box>;
}
function LeadCaptureFormSearchParams(props) {
  const $ = _c(8);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    const raw = searchParams?.get("email") ?? null;
    t0 = raw && validateEmail(raw) ? raw : null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const prefillEmail = t0;
  let t1;
  if ($[2] !== prefillEmail) {
    t1 = deriveUrlFromEmail(prefillEmail);
    $[2] = prefillEmail;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const prefillUrl = t1;
  let t2;
  if ($[4] !== prefillEmail || $[5] !== prefillUrl || $[6] !== props) {
    t2 = <LeadCaptureFormInner prefillEmail={prefillEmail} prefillUrl={prefillUrl} {...props} />;
    $[4] = prefillEmail;
    $[5] = prefillUrl;
    $[6] = props;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * LeadCaptureForm — a lightweight lead capture form molecule with state,
 * validation, and submission support.
 *
 * When a valid ?email= query parameter is present, the form enters prefill mode:
 * the email field is pre-populated and the secondary fields (first name, last name,
 * company, website) are hidden. Editing the email away from the prefilled value
 * restores the full form.
 *
 * @atomic-tier Molecule
 * @category Forms
 */
export const LeadCaptureForm = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<LeadCaptureFormInner prefillEmail={null} prefillUrl={null} {...props} />}><LeadCaptureFormSearchParams {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};